import { jsx as _jsx } from "react/jsx-runtime";
import { useBreakpointsContext } from "@air/provider-media-query";
import { useAirModal } from "@air/provider-modal";
import { useIsLoggedIn } from "@air/utils-auth";
import Router from "next/router";
import { memo, useCallback, useRef } from "react";
import { useHotkeys } from "react-hotkeys-hook";
import { useSelector } from "react-redux";
import { useMount } from "__barrel_optimize__?names=useMount!=!react-use";
import { BOUNDING_BOX_TEXT_WIDTH } from "~/components/BoundingBox/constants";
import { DiscussionForm } from "~/components/Discussions/DiscussionForm";
import { PublicCommentAnonymousSignUpModal } from "~/components/PublicLinkAnonymousSignUpModal/PublicCommentAnonymousSignUpModal";
import { ANNOTATION_COMMENT_FORM } from "~/constants/testIDs";
import { useURLBoardIdSelector } from "~/hooks/useURLBoardIdSelector";
import { useAnnotationContext } from "~/providers/AnnotationProvider/AnnotationProvider";
import { centralizedBoardAncestorsSelector } from "~/store/centralizedBoard/selectors";
import { centralizedClipWorkspaceIdSelector } from "~/store/centralizedClip/selectors";
import { activeTimestampParamSelector } from "~/store/router/selectors";
import { useGetDiscussionMentionsMembers } from "~/swr-hooks/discussions/useGetDiscussionMentionsMembers";
import { useSubmitNewDiscussion } from "~/swr-hooks/discussions/useSubmitNewDiscussion";
import { useAirSelector, useAirStore } from "~/utils/ReduxUtils";
export const FloatingCommentForm = /*#__PURE__*/ memo(()=>{
    const inputRef = useRef(null);
    const boardId = useURLBoardIdSelector();
    const boardAncestors = useAirSelector(centralizedBoardAncestorsSelector);
    var _useSelector;
    const clipWorkspaceId = (_useSelector = useSelector(centralizedClipWorkspaceIdSelector)) !== null && _useSelector !== void 0 ? _useSelector : "";
    const store = useAirStore();
    const mentionsList = useGetDiscussionMentionsMembers({
        boardId,
        ancestors: boardAncestors,
        workspaceId: clipWorkspaceId
    });
    const { submitNewDiscussion } = useSubmitNewDiscussion();
    const { clearNewAnnotation, getValue } = useAnnotationContext();
    const { isAboveMediumScreen } = useBreakpointsContext();
    const { isLoggedIn } = useIsLoggedIn();
    const [showSignUpModal] = useAirModal(PublicCommentAnonymousSignUpModal);
    useMount(()=>{
        var _inputRef_current;
        inputRef === null || inputRef === void 0 ? void 0 : (_inputRef_current = inputRef.current) === null || _inputRef_current === void 0 ? void 0 : _inputRef_current.focus();
    });
    useHotkeys("esc", ()=>{
        clearNewAnnotation();
    }, {
        enableOnFormTags: [
            "TEXTAREA"
        ]
    }, [
        clearNewAnnotation
    ]);
    const submitComment = useCallback((comment)=>{
        const createNewDiscussion = ()=>submitNewDiscussion({
                newComment: comment,
                timestamp: activeTimestampParamSelector(store.getState()),
                trackLocation: "asset-page",
                annotation: getValue().newAnnotation
            });
        if (isLoggedIn) {
            createNewDiscussion();
        } else {
            const ssoCallbackData = {
                url: Router.asPath,
                action: {
                    action: "create-public-discussion",
                    data: {
                        newComment: comment,
                        annotation: getValue().newAnnotation,
                        timestamp: activeTimestampParamSelector(store.getState()),
                        trackLocation: "asset-page"
                    }
                }
            };
            showSignUpModal({
                onLoginSuccess: createNewDiscussion,
                ssoCallbackData
            });
            clearNewAnnotation();
        }
    }, [
        clearNewAnnotation,
        getValue,
        isLoggedIn,
        showSignUpModal,
        store,
        submitNewDiscussion
    ]);
    return /*#__PURE__*/ _jsx("div", {
        className: "mx-3 rounded-4 border border-grey-5 bg-grey-1 shadow-popover [&>form]:border-none [&>form]:shadow-none [&>form]:focus-within:border-none [&>form]:focus-within:shadow-none",
        children: /*#__PURE__*/ _jsx(DiscussionForm, {
            "data-testid": ANNOTATION_COMMENT_FORM,
            ref: inputRef,
            onSubmit: submitComment,
            mentionsList: mentionsList,
            placeholder: "Share your thoughts",
            shouldShowMentionsButton: isAboveMediumScreen,
            style: {
                width: BOUNDING_BOX_TEXT_WIDTH
            }
        })
    });
});
FloatingCommentForm.displayName = "FloatingCommentForm";
